'use client'

import React, { useCallback, useRef } from 'react'

import './twin.css'
import { PAGE_TITLE, type View } from './twinContent'
import { TwinControlBar } from './ui/TwinControlBar'
import { TwinInfoPanel } from './ui/TwinInfoPanel'
import { TwinLoading } from './ui/TwinLoading'
import { useTwinEngagement } from './useTwinEngagement'
import { useTwinScene } from './useTwinScene'

// React owns the chrome; the scene owns the canvas and everything that changes per frame. The two
// meet at the controller returned by createTwinScene. See docs/unveiling.md#the-digital-twin.
export function TwinStage({ track }: { track: boolean }) {
  const rootRef = useRef<HTMLDivElement>(null)
  const noteInteraction = useTwinEngagement(track)
  const { state, panelId, controller, failed } = useTwinScene(rootRef, noteInteraction)

  const back = useCallback(() => controller.current?.goBack(), [controller])
  const forward = useCallback(() => controller.current?.goForward(), [controller])
  const jump = useCallback((view: View) => controller.current?.setView(view), [controller])
  const reset = useCallback(() => controller.current?.reset(), [controller])
  const closePanel = useCallback(() => controller.current?.closePanel(), [controller])

  return (
    <div id="vr-twin" ref={rootRef} className="min-h-0 flex-1">
      <TwinLoading visible={!state.ready} failed={failed} />
      <div id="canvas-holder" />
      <div id="vignette" />
      <h1 className="sr-only">{PAGE_TITLE}</h1>
      {/* The scene creates and positions the markers inside this layer every frame. */}
      <div id="hotspot-layer" />

      <TwinControlBar
        step={state.step}
        stepTotal={state.stepTotal}
        levelName={state.levelName}
        primaryLabel={state.forwardLabel}
        canGoBack={state.canGoBack}
        canGoForward={state.canGoForward}
        onBack={back}
        onForward={forward}
        onJump={jump}
        onReset={reset}
      />

      <TwinInfoPanel id={panelId} onClose={closePanel} />
    </div>
  )
}
